import {
  mergeTyped,
  useDataStream,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { mapTo } from 'rxjs/operators';
import { updateKoumuten } from '../../../api/fetch-functions/koumuten';
import { KoumutenAPIRequestBodyType } from '../../../api/types/koumuten/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken } from '../../../global-state/auth-state';
import { fetchKoumutenList } from '../../../global-state/database/koumuten-list';
import { IKoumutenType } from '../../../types/record/koumuten';
import { EditKoumutenDialogView } from './edit-koumuten-dialog-view';

const formInputStateConfig: FormInputStateConfigType<KoumutenAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.koumutenName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.koumutenName,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
  koumuten: IKoumutenType;
}

export const EditKoumutenDialog: FC<Props> = memo(
  ({ open, onClose, koumuten: koumutenInit }: Readonly<Props>) => {
    const token = useToken();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<IKoumutenType, KoumutenAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      koumutenName$,
      koumutenNameErrors$,
      koumutenNameIsValid$,
      koumutenNameOnInput
    ] = useFormInputState<IKoumutenType, KoumutenAPIRequestBodyType>(
      open,
      fetchResult,
      formInputStateConfig,
      koumutenInit.name
    );

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), koumutenNameIsValid$)
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);

    const koumutenName = useStreamValue(koumutenName$, koumutenInit.name);
    const koumutenNameErrors = useStreamValue(koumutenNameErrors$);

    /* event handler */

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (token !== undefined && koumutenName !== undefined) {
            updateKoumuten(token)(koumutenInit.id)({
              name: koumutenName
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchKoumutenList();
              }
            });
          }
        }
      },
      [
        onClose,
        koumutenName,
        koumutenInit,
        token,
        setFetchResult,
        submitOnClick
      ]
    );

    return (
      <EditKoumutenDialogView
        open={open}
        onClose={closeOnClick}
        koumutenName={koumutenName}
        koumutenNameErrors={koumutenNameErrors}
        koumutenNameOnInput={koumutenNameOnInput}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

EditKoumutenDialog.displayName = 'EditKoumutenDialog';
